import { EntityTypeEnum } from '../enums/entity.enum';
import {
  PublicReturnFeeEnum,
  PublicReturnGeneralClassificationEnum,
} from '../enums/periodicStream.enum';
import {
  CELL_IMPORT_STRATEGY_CONFIG,
  CELL_STYLE_FILL_PATTERN_CONFIG,
  CELL_STYLE_FILL_TYPE_CONFIG,
  CELL_VALUE_TYPE_CONFIG,
  COMMON_EXCEL_WORKBOOK_CONFIG,
  CONDITIONAL_FORMATTING_TYPE_CONFIG,
  FULL_SHEET_PROTECTION_CONFIG,
  SHEET_STATE_CONFIG,
  TWorkbookConfig,
  UTILS,
  WORKBOOK_IMPORT_STRATEGY_CONFIG,
} from '@aksia/infrastructure';
import { PeriodicStream } from '../classes/periodic-stream/periodic-stream';

export const PERIODIC_STREAMS = {
  GROSSIRR: 'grossIRR',
  NETIRR: 'netIRR',
  GROSSMOIC: 'grossMOIC',
  NETMOIC: 'netMOIC',
  NETDPI: 'netDPI',
  NETRVPI: 'netRVPI',
  INVESTED_CAPITAL: 'investedCapital',
  NETINCOMERETURN: 'netIncomeReturn',
  GROSSINCOMERETURN: 'grossIncomeReturn',
  NETAPPRECIATIONRETURN: 'netAppreciationReturn',
  GROSSAPPRECIATIONRETURN: 'grossAppreciationReturn',
  TOTALNETRETURN: 'totalNetReturn',
  TOTALGROSSRETURN: 'totalGrossReturn',
  SCALEUPRATIO: 'scaleUpRatio',
  PUBLIC_RETURNS: 'publicReturns',
  COMPANY_AUM: 'companyAum',
  PROGRAM_AUM: 'programAum',
  FUND_AUM: 'fundAum',
} as const;

export const PERIODIC_STREAM_GROUPS = {
  PRIVATE_RETURNS_OPEN_END_EXPAND:
    'grossIncomeReturn,grossAppreciationReturn,totalGrossReturn,netIncomeReturn,netAppreciationReturn,totalNetReturn',
  PRIVATE_RETURNS_CLOSED_END_EXPAND:
    'grossIRR,netIRR,grossMOIC,netMOIC,netDPI,netRVPI,investedCapital,scaleUpRatio',
  PRIVATE_RETURNS_EXPAND:
    'grossIRR,netIRR,grossMOIC,netMOIC,netDPI,netRVPI,investedCapital,netIncomeReturn,grossIncomeReturn,netAppreciationReturn,grossAppreciationReturn,totalNetReturn,totalGrossReturn,scaleUpRatio',
  AUM_EXPAND: 'companyAum,programAum,fundAum',
} as const;

export const PERIODIC_STREAM_REQUEST_MODE = {
  MINMAX: 'MINMAX',
  HISTORICAL: 'HISTORICAL',
  ASOF: 'ASOF',
  UPDATE: 'UPDATE',
  UPLOAD: 'UPLOAD',
} as const;

export const PERIODIC_STREAM_FIRST_ASOF_FALLBACK = {
  COMPANY_AUM: true,
  PROGRAM_AUM: true,
  FUND_AUM: false,
  PUBLIC_RETURNS: false,
  GROSSIRR: false,
  NETIRR: false,
  GROSSMOIC: false,
  NETMOIC: false,
  NETDPI: false,
  NETRVPI: false,
  INVESTED_CAPITAL: false,
  NETINCOMERETURN: false,
  GROSSINCOMERETURN: false,
  NETAPPRECIATIONRETURN: false,
  GROSSAPPRECIATIONRETURN: false,
  TOTALNETRETURN: false,
  TOTALGROSSRETURN: false,
  SCALEUPRATIO: false,
} as const;

export const PERIODIC_STREAM_URIS = {
  COMPANY_AUM: {
    MINMAX: 'aum/latestForEntityTree/{entityId}/1',
    HISTORICAL: 'aum/mergeStream/{entityId}/1',
    ASOF: 'aum/mergeStream/{entityId}/1',
    UPDATE: '',
    UPLOAD: '',
  },
  PROGRAM_AUM: {
    MINMAX: 'aum/latest/{entityId}/2',
    HISTORICAL: 'aum/mergeStream/{entityId}/2',
    ASOF: 'aum/mergeStream/{entityId}/2',
    UPDATE: '',
    UPLOAD: '',
  },
  FUND_AUM: {
    MINMAX: 'aum/latest/{entityId}/3',
    HISTORICAL: 'aum/mergeStream/{entityId}/3',
    ASOF: 'aum/mergeStream/{entityId}/3',
    UPDATE: '',
    UPLOAD: '',
  },
  PUBLIC_RETURNS: {
    MINMAX:
      'periodicdata/publicreturns/actual/2@{entityId}_{entityTypeId}/stream/rawwithytd',
    HISTORICAL:
      'periodicdata/publicreturns/actual/2@{entityId}_{entityTypeId}/stream/rawwithytd',
    ASOF: 'periodicdata/publicreturns/actual/2@{entityId}_{entityTypeId}/stream/rawwithytd',
    UPDATE: '',
    UPLOAD:
      'periodicdata/publicreturns/actual/{investmentProgramId}/stream/upload?fundId={entityId}',
  },
  SCALEUP_RATIO: {
    MINMAX: 'portco/funddeals/scaleUpRatio/{entityId}/stream/asofrange',
    HISTORICAL: 'portco/funddeals/scaleUpRatio/{entityId}',
    ASOF: 'portco/funddeals/scaleUpRatio/{entityId}/stream/datapoint?asOf={asOf}',
    UPDATE:
      'portco/funddeals/scaleUpRatio/{entityId}/stream/edit?periodicity={periodicity}',
    UPLOAD: '',
  },
  PRIVATE_RETURNS: {
    MINMAX:
      'periodicdata/returnprofile/allreturnprofilestreams/{entityId}/stream/asofrange',
    HISTORICAL: 'periodicdata/returnprofile/{streamType}/{entityId}',
    ASOF: 'periodicdata/returnprofile/allreturnprofilestreams/{entityId}/stream/datapoint?asOf={asOf}',
    UPDATE:
      'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
    UPLOAD: '',
  },
  URI: (
    entityId: number,
    entityTypeId: EntityTypeEnum,
    streamType: (typeof PERIODIC_STREAMS)[keyof typeof PERIODIC_STREAMS],
    requestType: (typeof PERIODIC_STREAM_REQUEST_MODE)[keyof typeof PERIODIC_STREAM_REQUEST_MODE],
    periodicity: string,
    asOf?: string,
    investmentProgramId?: number,
  ) => {
    let uri = '';
    if (
      PERIODIC_STREAM_GROUPS.PRIVATE_RETURNS_EXPAND.includes(streamType) &&
      streamType !== PERIODIC_STREAMS.SCALEUPRATIO
    ) {
      uri = PERIODIC_STREAM_URIS.PRIVATE_RETURNS[requestType];
    } else {
      switch (streamType) {
        case PERIODIC_STREAMS.COMPANY_AUM:
          uri = PERIODIC_STREAM_URIS.COMPANY_AUM[requestType];
          break;
        case PERIODIC_STREAMS.PROGRAM_AUM:
          uri = PERIODIC_STREAM_URIS.PROGRAM_AUM[requestType];
          break;
        case PERIODIC_STREAMS.FUND_AUM:
          uri = PERIODIC_STREAM_URIS.FUND_AUM[requestType];
          break;
        case PERIODIC_STREAMS.PUBLIC_RETURNS:
          uri = PERIODIC_STREAM_URIS.PUBLIC_RETURNS[requestType];
          break;
        case PERIODIC_STREAMS.SCALEUPRATIO:
          uri = PERIODIC_STREAM_URIS.SCALEUP_RATIO[requestType];
          break;
        default:
          uri = PERIODIC_STREAM_URIS.PRIVATE_RETURNS[requestType];
      }
    }
    uri = uri
      ?.replace('{entityId}', entityId.toString())
      ?.replace('{entityTypeId}', entityTypeId.toString())
      ?.replace('{streamType}', streamType)
      ?.replace('{periodicity}', periodicity ?? '')
      ?.replace('{asOf}', asOf ?? '')
      ?.replace('{investmentProgramId}', investmentProgramId?.toString() ?? '');
    return uri;
  },
};

export const PERIODIC_STREAM_LOADER_KEYS = {
  GROSSIRR: 'grossIRR',
  NETIRR: 'netIRR',
  GROSSMOIC: 'grossMOIC',
  NETMOIC: 'netMOIC',
  NETDPI: 'netDPI',
  NETRVPI: 'netRVPI',
  INVESTED_CAPITAL: 'investedCapital',
  NETINCOMERETURN: 'netIncomeReturn',
  GROSSINCOMERETURN: 'grossIncomeReturn',
  NETAPPRECIATIONRETURN: 'netAppreciationReturn',
  GROSSAPPRECIATIONRETURN: 'grossAppreciationReturn',
  TOTALNETRETURN: 'totalNetReturn',
  TOTALGROSSRETURN: 'totalGrossReturn',
  SCALEUPRATIO: 'scaleUpRatio',
  PUBLIC_RETURNS: 'publicReturns',
  COMPANY_AUM: 'companyAum',
  PROGRAM_AUM: 'programAum',
  FUND_AUM: 'fundAum',
} as const;

export const PERIODIC_STREAM_INSTANCES = {
  grossIRR: PeriodicStream,
  netIRR: PeriodicStream,
  grossMOIC: PeriodicStream,
  netMOIC: PeriodicStream,
  netDPI: PeriodicStream,
  netRVPI: PeriodicStream,
  investedCapital: PeriodicStream,
  netIncomeReturn: PeriodicStream,
  grossIncomeReturn: PeriodicStream,
  netAppreciationReturn: PeriodicStream,
  grossAppreciationReturn: PeriodicStream,
  totalNetReturn: PeriodicStream,
  totalGrossReturn: PeriodicStream,
  scaleUpRatio: PeriodicStream,
  publicReturns: PeriodicStream,
  companyAum: PeriodicStream,
  programAum: PeriodicStream,
  fundAum: PeriodicStream,
} as const;

export const PERIODIC_STREAM_FORMATING = {
  grossIRR: 'percent',
  netIRR: 'percent',
  grossMOIC: 'ratio',
  netMOIC: 'ratio',
  netDPI: 'ratio',
  netRVPI: 'ratio',
  investedCapital: 'currency',
  netIncomeReturn: 'percent',
  grossIncomeReturn: 'percent',
  netAppreciationReturn: 'percent',
  grossAppreciationReturn: 'percent',
  totalNetReturn: 'percent',
  totalGrossReturn: 'percent',
  scaleUpRatio: 'ratio',
  publicReturns: 'percent',
  companyAum: 'currency',
  programAum: 'currency',
  fundAum: 'currency',
};

export const AUM_EXCEL_WORKBOOK_CONFIG: TWorkbookConfig = {
  ...COMMON_EXCEL_WORKBOOK_CONFIG,
  category: 'AUM Streams',
  subject: '1.0',
  description: 'Import / Export file for periodic streams.',
  manager: 'datateam@aksia.com',
  importStrategy: WORKBOOK_IMPORT_STRATEGY_CONFIG.CLIENT,
  sheets: [
    {
      sheetName: 'AUM',
      state: SHEET_STATE_CONFIG.VISIBLE,
      tables: [
        {
          name: 'AUMTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'As of',
              filterButton: false,
            },
            {
              name: 'AUM',
              filterButton: false,
            },
            {
              name: 'Source',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'As of',
          field: 'asOf',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'AUMTable',
          width: 20,
          style: {
            numFmt: 'mm/dd/yyyy',
            alignment: { horizontal: 'left' },
          },
          dataValidation: {
            type: 'date',
            operator: 'lessThan',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: [UTILS.DATE.toEndOfMonth(new Date())!],
          },
          onExport: (value: Date | undefined) =>
            value ? new Date(UTILS.DATE.toLocalISOString(value, true)!) : value,
        },
        {
          cell: 'AUM',
          field: 'value',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'AUMTable',
          width: 20,
          style: {
            numFmt: '#,##0.00',
            alignment: { horizontal: 'left' },
          },
          dataValidation: {
            type: 'decimal',
            operator: 'greaterThanOrEqual',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: [0],
          },
        },
        {
          cell: 'Source',
          field: 'source',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'AUMTable',
          width: 40,
          style: { alignment: { horizontal: 'left' } },
          dataValidation: {
            type: 'list',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: ['=INDIRECT("SourcesTable[Source]")'],
          },
        },
      ],
      data: [],
    },
    {
      sheetName: 'Sources',
      state: SHEET_STATE_CONFIG.HIDDEN,
      tables: [
        {
          name: 'SourcesTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'Source',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'Source',
          field: 'label',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'SourcesTable',
          width: 100,
          style: { alignment: { horizontal: 'left' } },
        },
      ],
      data: [],
    },
  ],
};

export const PUBLIC_RETURNS_EXCEL_WORKBOOK_CONFIG: TWorkbookConfig = {
  ...COMMON_EXCEL_WORKBOOK_CONFIG,
  category: 'Public Returns Streams',
  subject: '1.0',
  description: 'Import / Export file for periodic streams.',
  manager: 'datateam@aksia.com',
  importStrategy: WORKBOOK_IMPORT_STRATEGY_CONFIG.SERVER,
  sheets: [
    {
      sheetName: 'ReturnAmount',
      state: SHEET_STATE_CONFIG.VISIBLE,
      tables: [
        {
          name: 'ReturnsTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'ReturnDate',
              filterButton: false,
            },
            {
              name: 'ReturnAmount',
              filterButton: false,
            },
            {
              name: 'ReturnSource',
              filterButton: false,
            },
            {
              name: 'ReturnFees',
              filterButton: false,
            },
            {
              name: 'ReturnEntityType',
              filterButton: false,
            },
            {
              name: 'Estimate ReturnDate',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'ReturnDate',
          field: 'asOf',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 20,
          style: {
            numFmt: 'mm/dd/yyyy',
            alignment: { horizontal: 'left' },
          },
          dataValidation: {
            type: 'date',
            operator: 'lessThan',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: [UTILS.DATE.toEndOfMonth(new Date())!],
          },
          onExport: (value: Date | undefined) =>
            value ? new Date(UTILS.DATE.toLocalISOString(value, true)!) : value,
        },
        {
          cell: 'ReturnAmount',
          field: 'value',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 20,
          style: { numFmt: '0.00%', alignment: { horizontal: 'left' } },
          dataValidation: {
            type: 'decimal',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: [],
          },
        },
        {
          cell: 'ReturnSource',
          field: 'source',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 40,
          style: { alignment: { horizontal: 'left' } },
          dataValidation: {
            type: 'list',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: ['=INDIRECT("SourcesTable[Source]")'],
          },
        },
        {
          cell: 'ReturnFees',
          field: 'feeType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 40,
          style: { alignment: { horizontal: 'left' } },
          dataValidation: {
            type: 'list',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: ['=INDIRECT("FeesTable[Fee]")'],
          },
          onExport: (value: any) =>
            PublicReturnFeeEnum.toOptions()?.find((opt) => opt.value === value)
              ?.label,
        },
        {
          cell: 'ReturnEntityType',
          field: 'classification',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 40,
          style: { alignment: { horizontal: 'left' } },
          dataValidation: {
            type: 'list',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: ['=INDIRECT("EntityTypesTable[EntityType]")'],
          },
          onExport: (value: any) =>
            PublicReturnGeneralClassificationEnum.toOptions()?.find(
              (opt) => opt.value === value,
            )?.label,
        },
        {
          cell: 'Estimate ReturnDate',
          field: 'estimationAsOf',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 20,
          style: {
            numFmt: 'mm/dd/yyyy',
            alignment: { horizontal: 'left' },
          },
          dataValidation: {
            type: 'date',
            allowBlank: true,
            formulae: [],
          },
          onExport: (value: Date | undefined) =>
            value ? new Date(UTILS.DATE.toLocalISOString(value, true)!) : value,
        },
      ],
      conditionalFormatting: [
        {
          ref: '$B:$B',
          rules: [
            {
              type: 'expression',
              priority: 1,
              formulae: ['INDIRECT("ReturnsTable[@ReturnAmount]")>0'],
              style: {
                font: { color: { argb: '00A63E' } },
              },
            },
            {
              type: 'expression',
              priority: 2,
              formulae: ['INDIRECT("ReturnsTable[@ReturnAmount]")<0'],
              style: {
                font: { color: { argb: 'E7000B' } },
              },
            },
            ...[
              'Backtested',
              'Carve Out',
              'Composite',
              'Fund Of One',
              'Internal Only',
            ].map((retType) => ({
              type: CONDITIONAL_FORMATTING_TYPE_CONFIG.EXPRESSION,
              priority: 3,
              formulae: [
                `INDIRECT("ReturnsTable[@ReturnEntityType]")="${retType}"`,
              ],
              style: {
                fill: {
                  type: CELL_STYLE_FILL_TYPE_CONFIG.PATTERN,
                  pattern: CELL_STYLE_FILL_PATTERN_CONFIG.SOLID,
                  bgColor: { argb: 'FF8904' },
                },
              },
            })),
            ...['Investment Program', 'Master'].map((retType) => ({
              type: CONDITIONAL_FORMATTING_TYPE_CONFIG.EXPRESSION,
              priority: 4,
              formulae: [
                `INDIRECT("ReturnsTable[@ReturnEntityType]")="${retType}"`,
              ],
              style: {
                fill: {
                  type: CELL_STYLE_FILL_TYPE_CONFIG.PATTERN,
                  pattern: CELL_STYLE_FILL_PATTERN_CONFIG.SOLID,
                  bgColor: { argb: 'FFD7A8' },
                },
              },
            })),
          ],
        },
      ],
      data: [],
    },
    {
      sheetName: 'Sources',
      state: SHEET_STATE_CONFIG.HIDDEN,
      tables: [
        {
          name: 'SourcesTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'Source',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'Source',
          field: 'label',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'SourcesTable',
          width: 100,
          style: { alignment: { horizontal: 'left' } },
        },
      ],
      data: [],
    },
    {
      sheetName: 'Fees',
      state: SHEET_STATE_CONFIG.HIDDEN,
      tables: [
        {
          name: 'FeesTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'Fee',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'Fee',
          field: 'label',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'FeesTable',
          width: 100,
          style: { alignment: { horizontal: 'left' } },
        },
      ],
      data: PublicReturnFeeEnum.toOptions(),
    },
    {
      sheetName: 'EntityTypes',
      state: SHEET_STATE_CONFIG.HIDDEN,
      tables: [
        {
          name: 'EntityTypesTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'EntityType',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'EntityType',
          field: 'label',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'EntityTypesTable',
          width: 100,
          style: { alignment: { horizontal: 'left' } },
        },
      ],
      data: PublicReturnGeneralClassificationEnum.toOptions(),
    },
  ],
};

export const PRIVATE_RETURNS_EXCEL_WORKBOOK_CONFIG: TWorkbookConfig = {
  ...COMMON_EXCEL_WORKBOOK_CONFIG,
  category: 'Private Returns Streams',
  subject: '1.0',
  description: 'Import / Export file for periodic streams.',
  manager: 'datateam@aksia.com',
  importStrategy: WORKBOOK_IMPORT_STRATEGY_CONFIG.CLIENT,
  sheets: [
    {
      sheetName: 'Returns',
      state: SHEET_STATE_CONFIG.VISIBLE,
      tables: [
        {
          name: 'ReturnsTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'As of',
              filterButton: false,
            },
            {
              name: 'Returns',
              filterButton: false,
            },
            {
              name: 'Source',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'As of',
          field: 'asOf',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 20,
          style: {
            numFmt: 'mm/dd/yyyy',
            alignment: { horizontal: 'left' },
          },
          dataValidation: {
            type: 'date',
            operator: 'lessThan',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: [UTILS.DATE.toEndOfMonth(new Date()) ?? null],
          },
          onExport: (value: Date | undefined) =>
            value ? new Date(UTILS.DATE.toLocalISOString(value, true)!) : value,
        },
        {
          cell: 'Returns',
          field: 'value',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 20,
          style: {
            numFmt: '0.0000%',
            alignment: { horizontal: 'left' },
          },
          dataValidation: {
            type: 'decimal',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: [],
          },
          onExport: (value: any) =>
            value !== undefined && value !== null ? value / 100 : value,
        },
        {
          cell: 'Source',
          field: 'source',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'ReturnsTable',
          width: 40,
          style: { alignment: { horizontal: 'left' } },
          dataValidation: {
            type: 'list',
            allowBlank: false,
            showInputMessage: true,
            showErrorMessage: true,
            formulae: ['=INDIRECT("SourcesTable[Source]")'],
          },
        },
      ],
      conditionalFormatting: [
        {
          ref: '$B:$B',
          rules: [
            {
              type: 'expression',
              priority: 1,
              formulae: ['INDIRECT("ReturnsTable[@Returns]")>0'],
              style: {
                font: { color: { argb: '00A63E' } },
              },
            },
            {
              type: 'expression',
              priority: 2,
              formulae: ['INDIRECT("ReturnsTable[@Returns]")<0'],
              style: {
                font: { color: { argb: 'E7000B' } },
              },
            },
          ],
        },
      ],
      data: [],
    },
    {
      sheetName: 'Sources',
      state: SHEET_STATE_CONFIG.HIDDEN,
      tables: [
        {
          name: 'SourcesTable',
          ref: 'A1',
          headerRow: true,
          columns: [
            {
              name: 'Source',
              filterButton: false,
            },
          ],
        },
      ],
      cells: [
        {
          cell: 'Source',
          field: 'label',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'SourcesTable',
          width: 100,
          style: { alignment: { horizontal: 'left' } },
        },
      ],
      data: [],
    },
  ],
};

export const PERIODIC_EXCEL_WORKBOOK_CONFIG = {
  aum: AUM_EXCEL_WORKBOOK_CONFIG,
  program_aum: AUM_EXCEL_WORKBOOK_CONFIG,
  public_returns: PUBLIC_RETURNS_EXCEL_WORKBOOK_CONFIG,
  private_returns: PRIVATE_RETURNS_EXCEL_WORKBOOK_CONFIG,
};
